import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { resolveAsset } from "../lib/assets";

/**
 * Custom link cards. Each link chooses between two display modes:
 *   - "button": full-width labelled pill stacked vertically. The default.
 *   - "icon":   compact square icon-only button, rendered inline (good for
 *               placing custom links alongside the socials row).
 *
 * Opens in a new tab with safe rel attributes.
 */
export function mountLinks(links: ProfileConfig["links"]): HTMLElement | null {
  if (links.length === 0) return null;

  const iconLinks = links.filter((l) => l.displayMode === "icon");
  const buttonLinks = links.filter((l) => l.displayMode !== "icon");

  const wrap = el("div", { class: "links" });
  if (iconLinks.length > 0) {
    const row = el("div", { class: "links-row" });
    for (const link of iconLinks) row.append(renderIcon(link));
    wrap.append(row);
  }
  if (buttonLinks.length > 0) {
    const stack = el("div", { class: "links-stack" });
    for (const link of buttonLinks) stack.append(renderButton(link));
    wrap.append(stack);
  }
  return wrap;
}

function renderButton(link: ProfileConfig["links"][number]): HTMLElement {
  const a = el("a", {
    class: "link-card",
    role: "listitem",
    href: link.url,
    target: "_blank",
    rel: "noopener noreferrer nofollow",
    style: link.glowColor ? `--link-glow:${link.glowColor}` : null,
    "data-tooltip": link.url,
  });
  if (link.icon) {
    a.append(
      el("img", {
        class: "link-card__icon",
        src: resolveAsset(link.icon),
        alt: "",
        draggable: false,
      }),
    );
  }
  a.append(el("span", { class: "link-card__label" }, [link.label]));
  return a;
}

function renderIcon(link: ProfileConfig["links"][number]): HTMLElement {
  const a = el("a", {
    class: "link-icon",
    href: link.url,
    target: "_blank",
    rel: "noopener noreferrer nofollow",
    style: link.glowColor ? `--icon-glow:${link.glowColor}` : null,
    "data-tooltip": `${link.label} · ${link.url.replace(/^https?:\/\//, "")}`,
    "aria-label": link.label,
  });
  if (link.icon) {
    a.append(
      el("img", {
        class: "link-icon__img",
        src: resolveAsset(link.icon),
        alt: "",
        draggable: false,
      }),
    );
  } else {
    // Fallback: use the first letter of the label as a glyph.
    a.append(
      el("span", { class: "link-icon__glyph" }, [link.label.slice(0, 1).toUpperCase()]),
    );
  }
  return a;
}
